/**
 * GuitarFinds — Collection Browser
 * Tabs switch between Liquid-rendered panels that use Shopify/Dawn native card-product.
 * No products.json card templating, so quick add, vendor, ratings, badges, and card settings stay native.
 */
(function () {
  'use strict';

  const SEE_MORE_ARROW =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  class CollectionBrowser {
    constructor(el) {
      this.el = el;
      this.tabs = Array.from(el.querySelectorAll('[data-cb-tab]'));
      this.panels = Array.from(el.querySelectorAll('[data-cb-panel]'));
      this.seeMore = el.querySelector('[data-cb-see-more]');
      this.abortController = new AbortController();

      if (!this.tabs.length || !this.panels.length) return;

      this.activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this._bindTabs();
      this._switchTab(this.activeTab, true);
    }

    _bindTabs() {
      for (const tab of this.tabs) {
        tab.addEventListener('click', () => this._switchTab(tab), { signal: this.abortController.signal });

        tab.addEventListener(
          'keydown',
          (event) => {
            const index = this.tabs.indexOf(tab);

            if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.tabs[(index + 1) % this.tabs.length].focus();
            }

            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.tabs[(index - 1 + this.tabs.length) % this.tabs.length].focus();
            }

            if (event.key === 'Home') {
              event.preventDefault();
              this.tabs[0].focus();
            }

            if (event.key === 'End') {
              event.preventDefault();
              this.tabs[this.tabs.length - 1].focus();
            }
          },
          { signal: this.abortController.signal }
        );
      }
    }

    _switchTab(tab, isInitialLoad = false) {
      if (!tab) return;

      const panelId = tab.getAttribute('aria-controls');

      for (const candidate of this.tabs) {
        const active = candidate === tab;
        candidate.classList.toggle('collection-browser__tab--active', active);
        candidate.setAttribute('aria-selected', String(active));
        candidate.setAttribute('tabindex', active ? '0' : '-1');
      }

      for (const panel of this.panels) {
        const active = panel.id === panelId;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      }

      this.activeTab = tab;

      if (!isInitialLoad) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      if (this.seeMore) {
        const label = tab.dataset.cbSeeMoreLabel || tab.dataset.cbLabel || tab.textContent.trim();
        this.seeMore.href = tab.dataset.cbUrl || '#';
        this.seeMore.innerHTML = `${this._escape(label)} ${SEE_MORE_ARROW}`;
      }
    }

    _escape(value) {
      return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[char]);
    }

    destroy() {
      this.abortController.abort();
    }
  }

  function init(root = document) {
    root.querySelectorAll('[data-collection-browser]').forEach((el) => {
      if (!el._collectionBrowser) el._collectionBrowser = new CollectionBrowser(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const el = event.target.querySelector('[data-collection-browser]');
    if (!el?._collectionBrowser) return;
    el._collectionBrowser.destroy();
    delete el._collectionBrowser;
  });
})();
